// The markup document model: the per-page item lists, selection, the active
// tool/color/text-style, the open text box, and an undoable ACTION history.
// Everything the editor mutates goes through here; rendering and pointer
// handling live elsewhere (DrawingLayer / markupRender).

import { useState } from 'react';
import {
  DEFAULT_MARKER_WIDTH_STEPS,
  DEFAULT_STROKE_WIDTH_STEPS,
  DEFAULT_TEXT_STYLE,
  itemAnchor,
  MAX_STROKE_WIDTH_STEPS,
  MIN_STROKE_WIDTH_STEPS,
  resizeItemToWidth,
  STROKE_WIDTH_STEP,
  translateItem,
  type IMarkupPoint,
  type ITextStyle,
  type ItemsByPage,
  type MarkupColor,
  type MarkupItem,
  type MarkupTool,
} from '../../../utils/markupTypes';
import { useTextBox } from './useTextBox';

type TextItem = Extract<MarkupItem, { kind: 'text' }>;

/** One reversible action. Entries address items by index, which is sound
 *  because undo/redo is strictly LIFO: at the moment an entry is applied or
 *  reversed, the list is in exactly the state it was recorded against. */
type HistoryEntry =
  | { action: 'add'; page: number; index: number; item: MarkupItem }
  | { action: 'erase'; page: number; index: number; item: MarkupItem }
  | { action: 'move'; page: number; index: number; from: IMarkupPoint; to: IMarkupPoint }
  | {
      action: 'moveMany';
      page: number;
      moves: { index: number; from: IMarkupPoint; to: IMarkupPoint }[];
    }
  | { action: 'restyle'; page: number; index: number; before: MarkupItem; after: MarkupItem }
  | { action: 'clear'; page: number; items: MarkupItem[] }
  | { action: 'eraseMany'; page: number; entries: { index: number; item: MarkupItem }[] };

/** onShowPage: brings a page into view — undo/redo turn to the page they change. */
export function useDrawingModel(currentPage: number, onShowPage?: (page: number) => void) {
  const [tool, setToolState] = useState<MarkupTool>('pen');
  const [color, setColor] = useState<MarkupColor>('red');
  const [textStyle, setTextStyle] = useState<ITextStyle>(DEFAULT_TEXT_STYLE);
  // Width is remembered per tool: a fat marker shouldn't fatten the pen.
  const [strokeWidths, setStrokeWidths] = useState<Partial<Record<MarkupTool, number>>>({
    marker: DEFAULT_MARKER_WIDTH_STEPS,
  });
  const [itemsByPage, setItemsByPage] = useState<ItemsByPage>({});
  const [undoStack, setUndoStack] = useState<HistoryEntry[]>([]);
  const [redoStack, setRedoStack] = useState<HistoryEntry[]>([]);
  // The selected items (indexes into the current page's items). The select
  // tool grabs any item, or several via a marquee drag; the text tool selects
  // one box at a time, whose format controls edit in place. Dragging moves
  // the selection either way.
  const [selectedIndices, setSelectedIndicesState] = useState<number[]>([]);
  const textBox = useTextBox();
  // A page flip drops the selection: its indexes belong to the page left behind.
  const [selectionPage, setSelectionPage] = useState(currentPage);
  if (selectionPage !== currentPage) {
    setSelectionPage(currentPage);
    setSelectedIndicesState([]);
  }
  /** The lone selected item's index, or null — used everywhere a single-item
   *  operation (resize, restyle, text edit) only makes sense for exactly one. */
  const selectedIndex = selectedIndices.length === 1 ? selectedIndices[0]! : null;
  const setSelectedIndex = (index: number | null) => setSelectedIndicesState(index === null ? [] : [index]);
  const setSelectedIndices = (indices: number[]) => setSelectedIndicesState(indices);

  const pageItems = itemsByPage[currentPage] ?? [];
  // Undo and redo wait while a text box is open: they can turn the page, and
  // the box belongs to the page it was typed on.
  const canUndo = undoStack.length > 0 && !textBox.box;
  const canRedo = redoStack.length > 0 && !textBox.box;

  const selectedItem = selectedIndex !== null ? pageItems[selectedIndex] ?? null : null;
  const selectedText = selectedItem?.kind === 'text' ? selectedItem : null;

  /** What the format controls show: the selected stamp's style, else the tool's. */
  const activeTextStyle: ITextStyle = selectedText
    ? {
        size: selectedText.size,
        bold: !!selectedText.bold,
        italic: !!selectedText.italic,
        underline: !!selectedText.underline,
        align: selectedText.align ?? 'left',
        direction: selectedText.direction ?? 'ltr',
      }
    : textStyle;

  /** The swatches reflect the selected item's color while one is selected. */
  const activeColor = selectedItem ? selectedItem.color : color;

  /** The active tool's width on the toolbar's 1–10 scale. */
  const strokeWidthSteps = strokeWidths[tool] ?? DEFAULT_STROKE_WIDTH_STEPS;
  /** The same width as the fraction of content width stored on stroke items. */
  const strokeWidth = strokeWidthSteps * STROKE_WIDTH_STEP;

  function changeStrokeWidth(steps: number) {
    const clamped = Math.min(MAX_STROKE_WIDTH_STEPS, Math.max(MIN_STROKE_WIDTH_STEPS, Math.round(steps)));
    setStrokeWidths((prev) => ({ ...prev, [tool]: clamped }));
  }

  const setTool = (next: MarkupTool) => {
    setSelectedIndicesState([]); // a tool switch drops the selection
    setToolState(next);
  };

  const withPage = (prev: ItemsByPage, pageNo: number, mutate: (list: MarkupItem[]) => void): ItemsByPage => {
    const list = [...(prev[pageNo] ?? [])];
    mutate(list);
    return { ...prev, [pageNo]: list };
  };

  /** The words in the open text box, as the stamp they'd become: null while it
   *  holds none (an emptied rewrite keeps the old words). */
  function typedMark(): { index: number | null; item: TextItem } | null {
    const { box, value } = textBox;
    const text = value.replace(/\s+$/, '');
    if (!box || text.trim() === '') return null;
    if (box.editIndex !== undefined) {
      const item = pageItems[box.editIndex];
      return item?.kind === 'text' ? { index: box.editIndex, item: { ...item, text } } : null;
    }
    return {
      index: null,
      item: {
        kind: 'text',
        color: activeColor,
        x: box.x,
        y: box.y,
        w: box.w,
        text,
        size: activeTextStyle.size,
        bold: activeTextStyle.bold,
        italic: activeTextStyle.italic,
        underline: activeTextStyle.underline,
        align: activeTextStyle.align,
        direction: activeTextStyle.direction,
      },
    };
  }

  const typed = typedMark();
  /** Every mark plus the words still in the text box — what a save stores. */
  const itemsToSave = typed
    ? withPage(itemsByPage, currentPage, (list) => {
      if (typed.index === null) list.push(typed.item);
      else list[typed.index] = typed.item;
    })
    : itemsByPage;
  const hasItems = Object.values(itemsToSave).some((list) => list.length > 0);

  /** Turns the typed words into a stamp (Enter, or a click away) and closes the box. */
  function settleText() {
    if (typed?.index === null) commit(typed.item);
    else if (typed) editText(typed.index, typed.item.text);
    textBox.close();
  }

  function pushAction(entry: HistoryEntry) {
    setUndoStack((prev) => [...prev, entry]);
    setRedoStack([]); // a new action forks history — the redo branch is gone
  }

  function commit(item: MarkupItem) {
    pushAction({ action: 'add', page: currentPage, index: pageItems.length, item });
    setItemsByPage((prev) => withPage(prev, currentPage, (list) => list.push(item)));
    setSelectedIndicesState([]);
  }

  function eraseAt(index: number) {
    const item = pageItems[index];
    if (!item) return;
    pushAction({ action: 'erase', page: currentPage, index, item });
    setItemsByPage((prev) => withPage(prev, currentPage, (list) => list.splice(index, 1)));
    setSelectedIndicesState([]);
  }

  /** Erases every currently selected item as one undoable action. */
  function eraseSelected() {
    if (selectedIndices.length === 0) return;
    if (selectedIndices.length === 1) {
      eraseAt(selectedIndices[0]!);
      return;
    }
    const sorted = [...selectedIndices].sort((a, b) => a - b);
    const entries = sorted
      .map((index) => ({ index, item: pageItems[index] }))
      .filter((e): e is { index: number; item: MarkupItem } => !!e.item);
    if (entries.length === 0) return;
    pushAction({ action: 'eraseMany', page: currentPage, entries });
    setItemsByPage((prev) => withPage(prev, currentPage, (list) => {
      // Highest index first so earlier splices don't shift later targets.
      for (let k = entries.length - 1; k >= 0; k -= 1) list.splice(entries[k]!.index, 1);
    }));
    setSelectedIndicesState([]);
  }

  /** Moves any item's anchor to `to` — boxes relocate, strokes translate. */
  function moveItem(index: number, to: IMarkupPoint) {
    const item = pageItems[index];
    if (!item) return;
    pushAction({ action: 'move', page: currentPage, index, from: itemAnchor(item), to });
    setItemsByPage((prev) => withPage(prev, currentPage, (list) => {
      const it = list[index];
      if (it) list[index] = translateItem(it, to);
    }));
  }

  /** Moves several items at once (a marquee-selected group dragged together)
   *  as one undoable action. */
  function moveItems(moves: { index: number; to: IMarkupPoint }[]) {
    const withFrom = moves
      .map((m) => {
        const item = pageItems[m.index];
        return item ? { index: m.index, from: itemAnchor(item), to: m.to } : null;
      })
      .filter((m): m is { index: number; from: IMarkupPoint; to: IMarkupPoint } => m !== null);
    if (withFrom.length === 0) return;
    pushAction({ action: 'moveMany', page: currentPage, moves: withFrom });
    setItemsByPage((prev) => withPage(prev, currentPage, (list) => {
      for (const m of withFrom) {
        const it = list[m.index];
        if (it) list[m.index] = translateItem(it, m.to);
      }
    }));
  }

  /** Replaces the item at `index` as one undoable action. */
  function restyleBox(index: number, after: MarkupItem) {
    const before = pageItems[index];
    if (!before) return;
    pushAction({ action: 'restyle', page: currentPage, index, before, after });
    setItemsByPage((prev) => withPage(prev, currentPage, (list) => { list[index] = after; }));
  }

  function restyleSelected(after: MarkupItem) {
    if (!selectedText || selectedIndex === null) return;
    restyleBox(selectedIndex, after);
  }

  function editText(index: number, text: string) {
    const item = pageItems[index];
    if (item?.kind !== 'text' || item.text === text) return;
    restyleBox(index, { ...item, text });
  }

  /** Resizes any item to bounding width `w` — strokes scale uniformly. */
  function resizeItem(index: number, w: number) {
    const item = pageItems[index];
    if (!item) return;
    const after = resizeItemToWidth(item, w);
    if (after !== item) restyleBox(index, after);
  }

  function changeTextStyle(patch: Partial<ITextStyle>) {
    // Always carried into the next stamp; also applied to the selected one.
    setTextStyle((s) => ({ ...s, ...patch }));
    if (selectedText) restyleSelected({ ...selectedText, ...patch });
  }

  function changeColor(next: MarkupColor) {
    // Carried into the next item; also recolors the selected one (undoably).
    setColor(next);
    if (selectedIndex !== null && selectedItem && selectedItem.color !== next) {
      restyleBox(selectedIndex, { ...selectedItem, color: next });
    }
  }

  /** Applies `entry` in reverse (undo) or forward (redo) to its page list. */
  function applyEntry(entry: HistoryEntry, reverse: boolean) {
    setItemsByPage((prev) => withPage(prev, entry.page, (list) => {
      switch (entry.action) {
        case 'add':
          if (reverse) list.splice(entry.index, 1);
          else list.splice(entry.index, 0, entry.item);
          break;
        case 'erase':
          if (reverse) list.splice(entry.index, 0, entry.item);
          else list.splice(entry.index, 1);
          break;
        case 'move': {
          const it = list[entry.index];
          if (it) list[entry.index] = translateItem(it, reverse ? entry.from : entry.to);
          break;
        }
        case 'moveMany':
          for (const m of entry.moves) {
            const it = list[m.index];
            if (it) list[m.index] = translateItem(it, reverse ? m.from : m.to);
          }
          break;
        case 'restyle':
          list[entry.index] = reverse ? entry.before : entry.after;
          break;
        case 'clear':
          list.splice(0, list.length, ...(reverse ? entry.items : []));
          break;
        case 'eraseMany': {
          const sorted = [...entry.entries].sort((a, b) => a.index - b.index);
          if (reverse) {
            for (const e of sorted) list.splice(e.index, 0, e.item);
          } else {
            for (let k = sorted.length - 1; k >= 0; k -= 1) list.splice(sorted[k]!.index, 1);
          }
          break;
        }
      }
    }));
  }

  function undo() {
    const entry = undoStack[undoStack.length - 1];
    if (!entry || !canUndo) return;
    setUndoStack((prev) => prev.slice(0, -1));
    setRedoStack((prev) => [...prev, entry]);
    setSelectedIndicesState([]); // indexes may shift under the selection
    applyEntry(entry, true);
    if (entry.page !== currentPage) onShowPage?.(entry.page);
  }

  function redo() {
    const entry = redoStack[redoStack.length - 1];
    if (!entry || !canRedo) return;
    setRedoStack((prev) => prev.slice(0, -1));
    setUndoStack((prev) => [...prev, entry]);
    setSelectedIndicesState([]);
    applyEntry(entry, false);
    if (entry.page !== currentPage) onShowPage?.(entry.page);
  }

  function clearPage() {
    if (pageItems.length === 0) return;
    // One undoable action carrying the whole page — undo brings it all back.
    pushAction({ action: 'clear', page: currentPage, items: pageItems });
    setItemsByPage((prev) => ({ ...prev, [currentPage]: [] }));
    setSelectedIndicesState([]);
  }

  /** Throws the whole drawing away — every page, the history with it. Used
   *  when a drawing session ends: the next one starts from nothing, whether the
   *  last was saved or discarded. */
  function reset() {
    setItemsByPage({});
    setUndoStack([]);
    setRedoStack([]);
    setSelectedIndicesState([]);
    textBox.close();
  }

  return {
    reset,
    textBox, settleText, itemsToSave,
    tool, setTool,
    activeColor, changeColor,
    activeTextStyle, changeTextStyle,
    textSelected: selectedText !== null,
    strokeWidth, strokeWidthSteps, changeStrokeWidth,
    itemsByPage, pageItems,
    hasItems,
    canUndo, canRedo,
    selectedIndex, setSelectedIndex,
    selectedIndices, setSelectedIndices,
    commit, eraseAt, eraseSelected, moveItem, moveItems, resizeItem,
    undo, redo, clearPage,
  };
}
